/**
 * La testa del lavoro sulla commessa (GI-237, GI-279): dove si e', a che punto
 * si e', che cosa si fa adesso. Una riga: il percorso — «Commesse › codice» —
 * la linea, lo stato in una parola, i conti e il prossimo passo. Tutto il resto
 * sta nelle schede accanto.
 *
 * **Il percorso e' il ritorno.** Prima dentro una commessa non c'era nessun
 * «indietro»: l'unica uscita era l'icona «Commesse» della barra, che qui e'
 * accesa come «sei qui». Un'uscita che sembra un indicatore non la trova
 * nessuno. Ora «Commesse» e' la prima parola della testa, ed e' un
 * collegamento; il codice della commessa e' il secondo, e porta alla
 * Panoramica. Quando la schermata non e' una scheda — la scheda dell'elemento,
 * per esempio — il suo nome e' il terzo anello, perche' nessuna scheda e'
 * accesa a dirlo.
 */
import { useId } from 'react'
import { Link } from 'react-router-dom'

import type { Whoami } from '../auth'
import { StatusPill, type PillTone } from '../carta/StatusPill'
import { decimal } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { stato } from '../InspectionList'
import type { QualityProgress } from '../quality'
import { choosePrimary } from '../routes/InspectionOverview'
import type { Commessa } from '../types'

export function WorkspaceHeader({
  inspectionId,
  commessa,
  progress,
  who,
  screen = null,
}: {
  inspectionId: string
  commessa: Commessa
  progress: QualityProgress | null
  who: Whoami
  /** Il nome della schermata, quando non e' una delle schede. */
  screen?: string | null
}): JSX.Element {
  const t = useT()
  const whyId = useId()
  const status = stato(commessa, t)
  const next = choosePrimary(who, inspectionId, commessa, progress)
  const line = [commessa.activity, commessa.plant_type, commessa.country].filter(Boolean).join(' · ')
  return (
    <section className="workspace-head" aria-label={t('chrome.commessa')}>
      <nav className="workspace-percorso" aria-label={t('crumb.path')}>
        <Link to="/work" className="workspace-percorso-radice">
          {t('rail.work')}
        </Link>
        <span className="workspace-percorso-sep" aria-hidden="true">
          ›
        </span>
        <Link to={`/inspections/${inspectionId}`} className="workspace-code t-num">
          {commessa.external_inspection_id}
        </Link>
        {screen !== null && (
          <>
            <span className="workspace-percorso-sep" aria-hidden="true">
              ›
            </span>
            <span className="workspace-percorso-schermata" aria-current="page">
              {screen}
            </span>
          </>
        )}
      </nav>
      <div className="workspace-identity">
        <span className="tenue">{line}</span>
        <StatusPill label={status.label} tone={status.tone as PillTone} />
        {commessa.phase ? <span className="tenue">· {commessa.phase}</span> : null}
      </div>
      <div className="workspace-counts tenue">
        <span>{t('workspace.toReview', { n: decimal(commessa.waiting) })}</span>
        <span>{t('workspace.decided', { n: decimal(commessa.decided) })}</span>
      </div>
      {next.href !== null && next.label !== null ? (
        <>
          <Link className="workspace-next" to={next.href} aria-describedby={next.reason ? whyId : undefined}>
            {next.label}
          </Link>
          {/* Il perche' del passo stava nel `title=` del comando: per chi tocca,
              tabula o ascolta non c'era (GI-281). Ora e' una riga accanto, che
              sotto i 1400 px resta ai soli lettori di schermo — la frase intera
              sta comunque sulla Panoramica. */}
          {next.reason ? (
            <span id={whyId} className="workspace-next-why tenue">
              {next.reason}
            </span>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
